import type { Theme } from "@mui/material";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DIALOG_WIDTH } from "../../constants";
import StandardDialogTitle from "./StandardDialogTitle";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Schemas } from "../../schemas";
import StandardDialogActions from "./StandardDialogActions";
import z from "zod";
import * as React from "react";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { useCreateAlert } from "../../hooks/mutation/create/useCreateAlert";
import { useState } from "react";
import { Integration } from "src/types";
import { useAlertsFromIntegration } from "../../hooks/query/useAlertsFromIntegration";
import { IntegrationPicker } from "../IntegrationPicker";

const schema = Schemas.alert.omit({ id: true });
type AlertFormValues = z.input<typeof schema>;

const NewAlertDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
}> = ({ onDialogClosed }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { mutateAsync: createAlert, isPending: isCreating } = useCreateAlert();
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [integration, setIntegration] = useState<Integration>();

  const {data: alertsFromProvider = [], isLoading} = useAlertsFromIntegration(integration);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AlertFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      integrationId: "",
    },
    mode: "onBlur",
  });

  const onSubmit = async (data: AlertFormValues) => {
    try {
      setSubmitError(null);
      await createAlert(schema.parse(data));
      reset({});
      onDialogClosed({ message: "Alert created!", severity: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onClose = () => {
    setSubmitError(null);
    setIntegration(undefined);
    reset({
      name: "",
      description: "",
      integrationId: "",
      foreignKeyValue: "",
    });
    onDialogClosed();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
      sx={{
        "& .MuiDialogTitle-root": {
          pb: 0.5,
        },
        "& .MuiDialogContent-root": {
          pt: 0.5,
        },
        "& .MuiDialogContent-root .MuiDialogActions-root": {
          marginTop: "20px",
        },
      }}
    >
      <StandardDialogTitle
        title={"New Alert"}
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box
        component="form"
        id="new-alert-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogContent sx={{ overflowX: "hidden", pt: 1.5 }}>
          <Stack
            spacing={2}
            sx={{
              width: "100%",
              maxWidth: isSmallScreen ? "100%" : 860,
              mx: "auto",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Create an alert and optionally declare its dependencies.
            </Typography>

            <Divider sx={{ mb: 0.5 }} />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "start",
              }}
            >
              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Payments Service"
                  error={!!errors.name}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>
                  {errors.name?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl error={!!errors.integrationId}>
                <FormLabel>Integration Id</FormLabel>

                <Controller
                  name="integrationId"
                  control={control}
                  render={({ field }) => (
                    <IntegrationPicker
                      value={field.value ?? ""}
                      onChange={(e) => {
                        field.onChange(e.id);
                        setIntegration(e);
                        if (submitError) {
                          setSubmitError(null);
                        }
                      }}
                    />
                  )}
                />

                <FormHelperText>
                  {errors.integrationId?.message ?? " "}
                </FormHelperText>
              </FormControl>

              {!integration && (
                <FormControl sx={{ gridColumn: "1 / -1" }}>
                  <FormLabel>Custom Alert ID</FormLabel>
                  <TextField
                    size="small"
                    placeholder="The key that identify the alert in a unique way"
                    error={!!errors.foreignKeyValue}
                    {...register("foreignKeyValue", {
                      onChange: () => submitError && setSubmitError(null),
                    })}
                  />
                  <FormHelperText error={!!errors.foreignKeyValue}>
                    {errors.foreignKeyValue?.message ?? " "}
                  </FormHelperText>
                </FormControl>
              )}

              {integration && (
                <FormControl sx={{ gridColumn: "1 / -1" }}>
                  <FormLabel>Alert</FormLabel>
                  {alertsFromProvider.length > 0 &&
                    integration.system === true && (
                      <Select
                        error={!!errors.foreignKeyValue}
                        {...register("foreignKeyValue", {
                          onChange: () => submitError && setSubmitError(null),
                        })}
                      >
                        {alertsFromProvider.map((a) => (
                          <MenuItem key={a.id} value={a.label}>
                            <ListItemText primary={a.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  {alertsFromProvider.length === 0 &&
                    !isLoading &&
                    integration.system === true && (
                      <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                        No alerts found for the selected integration.
                      </Typography>
                    )}
                  {isLoading && (
                    <Box sx={{ minHeight: 40, display: "flex", alignItems: "center" }}>
                      <CircularProgress size={18} />
                    </Box>
                  )}
                  {integration.system !== true && (
                    <TextField
                      size="small"
                      placeholder="The key that identify the alert in a unique way"
                      error={!!errors.foreignKeyValue}
                      {...register("foreignKeyValue", {
                        onChange: () => submitError && setSubmitError(null),
                      })}
                    />
                  )}
                  <FormHelperText error={!!errors.foreignKeyValue}>
                    {errors.foreignKeyValue?.message ?? " "}
                  </FormHelperText>
                </FormControl>
              )}

              <FormControl sx={{ gridColumn: "1 / -1" }}>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Short description of the system"
                  error={!!errors.description}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>
            </Box>

            {submitError && (
              <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                {submitError}
              </FormHelperText>
            )}
          </Stack>
        </DialogContent>

        <StandardDialogActions
          primaryText={"Create"}
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isCreating}
          disabled={isCreating}
        />
      </Box>
    </Dialog>
  );
};

export default NewAlertDialog;
